import BoltIcon from "@mui/icons-material/Bolt"
import PowerIcon from "@mui/icons-material/Power"
import RedoIcon from "@mui/icons-material/Redo"
import TimelineIcon from "@mui/icons-material/Timeline"
import {
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material"
import { ReactElement } from "react"

import { IDnoData } from "../../services/middleware/types/dnoInterface/dnoHomepageTypes"
import { AppCard, AppCardContent, AppCardHeader } from "../surfaces/appCard"
import AppPaper from "../surfaces/appPaper"

interface AdditionalKpiCardProps {
    cardTitle: string
    cardDescription: string
    data: IDnoData
}

/**
 * A card component that displays additional KPIs.
 *
 * @component
 * @param {string} cardTitle - The title of the card.
 * @param {string} cardDescription - The description of the card.
 * @param {string} data - The data to be displayed on the card.
 * @returns {ReactElement} The rendered additional KPI card component.
 */
const AdditionalKpiCard = ({
    cardTitle,
    cardDescription,
    data,
}: AdditionalKpiCardProps): ReactElement => (
    <AppPaper sx={{ flexGrow: 1, height: "100%" }}>
        <AppCard sx={{ padding: 0 }}>
            <AppCardHeader
                title={cardTitle}
                subheader={cardDescription}
                titleTypographyProps={{
                    variant: "h5",
                    fontWeight: "500",
                }}
            />
            <AppCardContent>
                <List
                    sx={{
                        p: 0,
                        width: "100%",
                        backgroundColor: "background.paper",
                    }}
                >
                    {/* List all data you want to appear in the card here */}
                    <ListItem>
                        <ListItemIcon>
                            <BoltIcon />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ variant: "h6" }}
                            primary={
                                <strong>{`${data.installedCapacity30DayAvg} kW`}</strong>
                            }
                            secondary="Average Application Installed Capacity (over the last 30 days)"
                        />
                    </ListItem>
                    <Divider variant="middle" component="li" />
                    <ListItem>
                        <ListItemIcon>
                            <PowerIcon />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ variant: "h6" }}
                            primary={
                                <strong>{`${data.demand30DayAvg} Amps`}</strong>
                            }
                            secondary="Average Application Total Property Maximum Demand (over the last 30 days)"
                        />
                    </ListItem>
                    <Divider variant="middle" component="li" />
                    <ListItem>
                        <ListItemIcon>
                            <RedoIcon />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ variant: "h6" }}
                            primary={<strong>{`${data.totCxAccepted}`}</strong>}
                            secondary="Total application which have completed commissioning process (all time)"
                        />
                    </ListItem>
                    <Divider variant="middle" component="li" />
                    <ListItem>
                        <ListItemIcon>
                            <TimelineIcon />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ variant: "h6" }}
                            primary={
                                <strong>{`${data.totApplications}`}</strong>
                            }
                            secondary="Total Applications (all time)"
                        />
                    </ListItem>
                    <Divider variant="middle" component="li" />
                </List>
            </AppCardContent>
        </AppCard>
    </AppPaper>
)

export default AdditionalKpiCard
